// ESPN fantasy-football id tables: positions, lineup slots, and scoring stats.
//
// SOURCE OF RECORD: docs/planning/espn-constants-provenance.md (derived Jul 27,
// 2026). Every row here is transcribed from that document — §1 for the
// position tables and LINEUP_SLOT_MAP, §3/§4/§6 for the stat maps. Nothing
// in this file was written
// from memory, and nothing may be "corrected" here. If a value looks wrong,
// re-derive it per §8 of the provenance doc and fix it THERE first; this file
// follows that document, never the other way around.
//
// Three traps the provenance doc verified against independent data, all of
// which the obvious/commonly-cited mapping gets WRONG:
//
//   1. FIELD-GOAL BUCKETS RUN DESCENDING. 74 = 50+, 80 = 0-39. This is verified
//      against nflverse play-by-play at every bucket (§4), not a typo. Do not
//      "fix" it to ascending — that scores a 55-yard field goal as a 20-yarder.
//   2. RECEPTIONS ARE STAT 53, NOT 41. Both hold the same value; only 53 appears
//      in scoringItems. Keying on 41 silently imports every PPR league as
//      non-PPR (§3).
//   3. THE POINTS-ALLOWED LADDER IS NOT CONTIGUOUS. It runs 89-92 then jumps to
//      121-125. Ids 93 and 94 sit inside that numeric range but are not
//      points-allowed tiers, so assuming a contiguous 89-97 ladder misreads
//      interceptions (95) as a points-allowed bucket (§6).
//
// statIds 63 (6 pts, offense) and 94 (D/ST) are deliberately UNMAPPED — neither
// was isolated to a verified event, and a wrong mapping is worse than a missing
// one (§7.3). Do not guess at them.

// ── Positions (provenance §1, "defaultPositionId → position") ─────────────
//
// Read from the same payload as the slot table below, `settings.positions`.
//
// TWO CONSTANTS, BECAUSE THESE ARE TWO DIFFERENT KINDS OF THING:
//
//   ESPN_POSITION_LABELS  is DATA. What ESPN publishes, quoted verbatim.
//                         Nobody decided it; it is not ours to change.
//   ESPN_POSITION_MAP     is a DECISION. Which ESPN positions Mock1 can
//                         represent, and what Mock1 calls them.
//
// They agree on almost every row and disagree on exactly one — id 16, where
// ESPN says "D/ST" and Mock1 says "DEF". Merging them into a single table is
// precisely what produced that bug: the merged version printed ESPN's column
// while labelling it "position", so callers got "D/ST" where Mock1's own
// vocabulary has no such token. Keep them apart. A structure that answers two
// questions answers one of them wrong, which is the same failure the
// ESPN_STAT_MAP / ESPN_DST_STAT_MAP split fixed.
//
// WHY EITHER LIVES HERE AND NOT IN THE API LAYER: api/espn/players.js returns
// `posId` as ESPN's raw defaultPositionId, passed through UNTRANSLATED, and
// says so — "the consumer owns it. Do not add a lookup table here." This is
// that consumer-side table. Keep the split intentional: the endpoint stays a
// dumb passthrough, and every translation decision is made in one place, next
// to the provenance record that justifies it.
//
// Ids 0, 6 and 8 are out of BOTH. ESPN returns placeholder labels (POS0 /
// POS6 / POS8) for them, which mean nothing. A caller that gets `undefined`
// is correctly informed the position is unknown; a caller that gets "POS6"
// has been handed garbage it will try to use.

// ESPN defaultPositionId → Mock1 position string.
//
// ONLY the six positions Mock1 can actually price. Punter (7), IDP (9-13, 17),
// head coach (14) and team QB (15) are deliberately absent: Mock1 has no
// concept of them, so `undefined` is the correct and honest answer. Do not add
// them with invented strings — a caller checking `if (pos)` handles a missing
// position, while a caller handed "LB" will try to draft one.
//
// ⚠️ Id 16 maps to "DEF", NOT ESPN's "D/ST". This single row is the reason
// this constant exists at all. "DEF" is Mock1's token — 32 occurrences in
// src/data/players.js, plus DEFAULT_SETTINGS.positions and STAT_BASELINES —
// and "D/ST" appears nowhere in Mock1's vocabulary. Use ESPN_POSITION_LABELS
// if you want what ESPN actually said.
export const ESPN_POSITION_MAP = {
  1:  "QB",
  2:  "RB",
  3:  "WR",
  4:  "TE",
  5:  "K",
  16: "DEF",  // ESPN publishes "D/ST" — see above
};

// ESPN defaultPositionId → ESPN's own abbrev, VERBATIM.
//
// Published data, transcribed exactly: id 16 reads "D/ST" here because that is
// what ESPN returns. This is NOT a mapping and must never be used as one —
// most of these ids have no Mock1 equivalent, and the one that does (16) is
// spelled differently. It exists for diagnostics and import notes: telling a
// user "this league rosters IDP (LB, CB, S), which Mock1 cannot import" needs
// ESPN's own words, not ours.
export const ESPN_POSITION_LABELS = {
  1:  "QB",
  2:  "RB",
  3:  "WR",
  4:  "TE",
  5:  "K",
  7:  "P",
  9:  "DT",
  10: "DE",
  11: "LB",
  12: "CB",
  13: "S",
  14: "HC",
  15: "TQB",
  16: "D/ST",
  17: "EDR",
};

// ── Lineup slots (provenance §1) ──────────────────────────────────────────
//
// Read from ESPN's own published metadata:
//   GET /apis/v3/games/ffl/seasons/2025?view=chui_default → settings.lineupSlots
// ESPN publishes id, abbrev, and eligiblePositions directly, so this is
// authoritative rather than inferred.
//
// eligiblePositions holds defaultPositionId values — the keys of
// ESPN_POSITION_LABELS above.
//
// Slots 20 (BE), 21 (IR) and 25 (ALL) enumerate every rosterable position;
// ESPN does not publish an "(all)" shorthand for them. An earlier revision of
// this file recorded them as `null` on that mistaken reading, which hid the
// isSuperflexSlot bug below — the real lists are what make bench-like slots
// distinguishable from a genuine offensive flex. Slot 22 (INV) publishes an
// empty list, which is different again and is recorded as [].
const ALL_ROSTERABLE_POSITION_IDS = Object.freeze(
  [1, 2, 3, 4, 5, 7, 9, 10, 11, 12, 13, 14, 15, 16, 17],
);

export const LINEUP_SLOT_MAP = {
  0:  { abbrev: "QB",   eligiblePositions: [1] },
  1:  { abbrev: "TQB",  eligiblePositions: [15] },
  2:  { abbrev: "RB",   eligiblePositions: [2] },
  3:  { abbrev: "RB/WR", eligiblePositions: [2, 3] },
  4:  { abbrev: "WR",   eligiblePositions: [3] },
  5:  { abbrev: "WR/TE", eligiblePositions: [3, 4] },
  6:  { abbrev: "TE",   eligiblePositions: [4] },
  7:  { abbrev: "OP",   eligiblePositions: [1, 2, 3, 4] },
  8:  { abbrev: "DT",   eligiblePositions: [9] },
  9:  { abbrev: "DE",   eligiblePositions: [10] },
  10: { abbrev: "LB",   eligiblePositions: [11] },
  11: { abbrev: "DL",   eligiblePositions: [9, 10] },
  12: { abbrev: "CB",   eligiblePositions: [12] },
  13: { abbrev: "S",    eligiblePositions: [13] },
  14: { abbrev: "DB",   eligiblePositions: [12, 13] },
  15: { abbrev: "DP",   eligiblePositions: [9, 10, 11, 12, 13] },
  16: { abbrev: "D/ST", eligiblePositions: [16] },
  17: { abbrev: "K",    eligiblePositions: [5] },
  18: { abbrev: "P",    eligiblePositions: [7] },
  19: { abbrev: "HC",   eligiblePositions: [14] },
  20: { abbrev: "BE",   eligiblePositions: ALL_ROSTERABLE_POSITION_IDS },
  21: { abbrev: "IR",   eligiblePositions: ALL_ROSTERABLE_POSITION_IDS },
  22: { abbrev: "INV",  eligiblePositions: [] },
  23: { abbrev: "FLEX", eligiblePositions: [2, 3, 4] },
  24: { abbrev: "EDR",  eligiblePositions: [17] },
  25: { abbrev: "ALL",  eligiblePositions: ALL_ROSTERABLE_POSITION_IDS },
};

// Superflex detection — DERIVED, never hardcoded to slot 7 (provenance §1).
//
// A slot is superflex iff its eligiblePositions are NON-EMPTY, a SUBSET of
// {1,2,3,4}, include 1 (QB), and include at least one of 2/3/4 (RB/WR/TE).
//
// ⚠️ THE SUBSET CLAUSE IS LOAD-BEARING. Without it the rule reads "includes 1 and
// any of 2/3/4", which is WRONG against real ESPN data: slots 20 (BE), 21 (IR)
// and 25 (ALL) enumerate every rosterable position, so they include 1 and 2 and
// satisfy it. Bench always has a non-zero count, so every ESPN league would
// import as superflex. What separates a bench from a flex is not what it
// admits but what it EXCLUDES — bench-like slots admit K (5), P (7) and
// IDP/D-ST (9-17), and no genuine offensive flex ever does.
//
// The rule stays settings-derived with no hardcoded ids, per ARCHITECTURE.md,
// so a future ESPN slot with the same shape is picked up for free. Against the
// live 26-slot table exactly one slot returns true: 7 (OP). Slot 23 (FLEX) is
// [2,3,4] — a subset, but no QB. Slot 0 (QB) is [1] — a subset with a QB, but
// no skill position. Both correctly false.
//
// Accepts either a slot id (looked up in LINEUP_SLOT_MAP) or a slot object with
// an eligiblePositions array — ESPN league payloads hand you the object, saved
// roster configs hand you the id. The object path is why the subset clause
// matters in practice: a caller passing ESPN's own bench slot straight through
// is exactly the case the old rule got wrong.
const FLEX_POSITION_IDS = [1, 2, 3, 4];
const SKILL_FLEX_POSITION_IDS = [2, 3, 4];

export function isSuperflexSlot(slot) {
  const entry = typeof slot === "number" ? LINEUP_SLOT_MAP[slot] : slot;
  const eligible = entry?.eligiblePositions;
  if (!Array.isArray(eligible) || eligible.length === 0) return false;
  if (!eligible.every((p) => FLEX_POSITION_IDS.includes(p))) return false;
  return eligible.includes(1) && SKILL_FLEX_POSITION_IDS.some((p) => eligible.includes(p));
}

// Flex detection — DERIVED, never hardcoded to slot 23 (provenance §1).
//
// A slot is FLEX iff its eligiblePositions are NON-EMPTY, have LENGTH > 1, and
// every element is in {2,3,4} (RB/WR/TE).
//
// ⚠️ THE LENGTH > 1 CLAUSE IS LOAD-BEARING. A dedicated RB slot is [2] and a
// dedicated TE slot is [4] — both are subsets of {2,3,4}, so without the length
// check every single-position skill slot reads as flex, and a standard roster
// imports 2RB/2WR/1TE as five flex spots.
//
// Zero overlap with isSuperflexSlot by construction: QB (1) is required for
// superflex and disqualifies a slot from flex, so no slot can satisfy both.
//
// Against the live 26-slot table exactly three slots return true: 3 (RB/WR),
// 5 (WR/TE) and 23 (FLEX). Mapping only 23 and dropping 3 and 5 silently
// SHRINKS THE ROSTER — a 12-team league with one RB/WR slot would import 12
// starters short, changing total demand and repricing the whole auction. Mock1's
// FLEX is RB/WR/TE, so absorbing the narrower 3 and 5 is slightly more
// permissive than ESPN; a right-sized roster with generalized eligibility beats
// a right-eligibility roster of the wrong size.
//
// Accepts either a slot id (looked up in LINEUP_SLOT_MAP) or a slot object with
// an eligiblePositions array — same contract as isSuperflexSlot.
export function isFlexSlot(slot) {
  const entry = typeof slot === "number" ? LINEUP_SLOT_MAP[slot] : slot;
  const eligible = entry?.eligiblePositions;
  if (!Array.isArray(eligible) || eligible.length <= 1) return false;
  return eligible.every((p) => SKILL_FLEX_POSITION_IDS.includes(p));
}

// ── Scoring stats (provenance §3 offense, §4 kicking, §6 D/ST) ─────────────
//
// TWO maps, split by SCOPE, because the parser reads the point value from a
// different field for each and because a handful of statIds mean different
// things depending on which entity the scoring item applies to:
//
//   ESPN_STAT_MAP      player scope — read scoringItems[].points
//   ESPN_DST_STAT_MAP  D/ST scope   — read scoringItems[].pointsOverrides["16"]
//
// The override is not optional (provenance §2 parser rule): D/ST scoring items
// carry points: 0 with the real value in pointsOverrides["16"], so reading
// `points` for a D/ST item silently zeroes out all defense scoring.
//
// Both maps share one shape: statId → { keys, type }
//
//   keys  Mock1 scoring keys. ALWAYS AN ARRAY — one element for almost every
//         stat, three for statId 80 (see the field-goal note below). Consumers
//         iterate it unconditionally; there is no string form to special-case.
//   type  'yds' — a per-yard stat (Mock1's *YdsPer keys)
//         'pts' — a per-event stat
//         'def' — a D/ST stat; every entry in ESPN_DST_STAT_MAP carries this
//
//   supersededBy  OPTIONAL statId. When present, this entry is a FALLBACK: a
//         consumer applies it ONLY when no scoring item with the named statId
//         appears in the same scoringItems array. Two ids can map to the same
//         Mock1 key at different granularities (74 vs 198 → fgMade50), and
//         ESPN's item order is arbitrary — a plain loop would let whichever
//         appeared last win. Check for the superseding id across the WHOLE
//         array before applying a fallback, not just the items seen so far.
//
// Ids present in ESPN's payload but absent from both maps are absent ON
// PURPOSE: either they are derived/duplicate stats the doc marks "do not use"
// (41 receptions mirror, 58 targets, 39/40/60/61 rate stats, 0/2/23 attempt
// counts, 210 games played), or they have no Mock1 equivalent (the 128-136
// yards-allowed ladder, FG attempt/miss splits, D/ST 120/127/187), or they are
// the two unidentified ids 63 and 94. Adding a row to either map means updating
// the provenance doc first.
export const ESPN_STAT_MAP = {
  // — Offense (§3). Reconciled against fantasy_points_2025.db season totals. —
  1:  { keys: ["passComp"],   type: "pts" },
  3:  { keys: ["passYdsPer"], type: "yds" },
  4:  { keys: ["passTD"],     type: "pts" },
  19: { keys: ["twoPtConv"],  type: "pts" },  // 2-pt pass
  20: { keys: ["passInt"],    type: "pts" },
  24: { keys: ["rushYdsPer"], type: "yds" },
  25: { keys: ["rushTD"],     type: "pts" },
  26: { keys: ["twoPtConv"],  type: "pts" },  // 2-pt rush
  42: { keys: ["recYdsPer"],  type: "yds" },
  43: { keys: ["recTD"],      type: "pts" },
  44: { keys: ["twoPtConv"],  type: "pts" },  // 2-pt reception
  // TRAP: 41 holds the identical value but never appears in scoringItems.
  // PPR detection must read 53. See the header note.
  53: { keys: ["receptions"], type: "pts" },

  // FUMBLES — CONVENTION CLASH, do not copy these through directly (§5).
  // ESPN is ADDITIVE: 68 = ALL fumbles (lost ones included), 72 = the lost
  // subset, so a lost fumble fires BOTH. Mock1 uses Convention B (partition):
  // scoring.fumbles scores NON-lost fumbles only. The conversion is
  //     scoring.fumbles     = espn68;
  //     scoring.fumblesLost = espn68 + espn72;
  // Copying 68→fumbles and 72→fumblesLost straight across UNDER-PENALIZES
  // every lost fumble. In the default league 68 is absent, so this bug never
  // shows up in default-league testing — only in leagues that score all fumbles.
  68: { keys: ["fumbles"],     type: "pts" },
  72: { keys: ["fumblesLost"], type: "pts" },

  // Return TDs (§3): 101 = kickoff return TD, 102 = punt return TD. Reconciled
  // exactly against play-by-play (Shaheed 1 KR, Dike 2 PR).
  //
  // These two ids appear in ESPN_DST_STAT_MAP as well, under `defTD`. That is
  // not a conflict: a kick or punt returned for a touchdown is ONE event, and
  // ESPN credits it to whichever entity did the returning. A returner who is a
  // rostered player scores it as returnTD; the same event by a D/ST unit scores
  // as defTD. Same id, same event, different entity — so it belongs in both
  // maps, keyed to whatever that scope calls it.
  101: { keys: ["returnTD"], type: "pts" },
  102: { keys: ["returnTD"], type: "pts" },

  // — Kicking (§4). THE BUCKETS RUN DESCENDING — verified, not a typo. —
  //
  // ESPN 80 = FG made 0-39. Mock1 splits that range into three keys and ESPN
  // cannot distinguish them, so the same value is written to all three rather
  // than inventing a split. This is the only entry carrying more than one key.
  // 81/82 (att/missed) are unmapped.
  80: { keys: ["fgMade0_19", "fgMade20_29", "fgMade30_39"], type: "pts" },
  77: { keys: ["fgMade40_49"], type: "pts" },  // 40-49; 78/79 att/missed unmapped
  198: { keys: ["fgMade50"],   type: "pts" },  // 50-59; 199/200 att/missed unmapped
  // 74 is the 50+ AGGREGATE, and maps to the same fgMade50 key as 198. It is a
  // FALLBACK: apply it only when the scoringItems array contains no item with
  // statId 198. 198 is preferred because 50-59 is the overwhelming majority of
  // 50+ attempts (Aubrey: 13 of 17), making it the better single-value
  // representative. supersededBy encodes that precedence so a parser looping
  // scoringItems can't let arbitrary item order decide it.
  74: { keys: ["fgMade50"], type: "pts", supersededBy: 198 },
  // 201/202/203 (60+) are DROPPED: Mock1 has no 60+ key and 60+ attempts run
  // ~1/season/kicker. Do not fold 201 into fgMade50 — that inflates every
  // 50-59 kick.
  85: { keys: ["fgMissed"],  type: "pts" },  // total missed; 83/84 unmapped
  86: { keys: ["patMade"],   type: "pts" },
  88: { keys: ["patMissed"], type: "pts" },  // 87 (att) unmapped
};

// D/ST scope (provenance §6). Reconciled against nflverse play-by-play for
// DEN/HOU/MIN/SEA.
//
// READ pointsOverrides["16"], NOT points — see the header note. Every entry
// here is type 'def'.
export const ESPN_DST_STAT_MAP = {
  99: { keys: ["defSack"],      type: "def" },
  95: { keys: ["defInt"],       type: "def" },
  96: { keys: ["defFumRec"],    type: "def" },
  97: { keys: ["defBlockKick"], type: "def" },
  98: { keys: ["defSafety"],    type: "def" },

  // Defensive TDs: 93, 101, 102, 103, 104 are all worth 6 and all mean defTD.
  // They were deliberately NOT disambiguated — Mock1 has one defTD key and
  // every candidate carries the same value, so disambiguation buys nothing and
  // risks a wrong guess. Take whichever is present; prefer 103/104 if several.
  //
  // 101/102 also sit in ESPN_STAT_MAP as returnTD. Same event, different
  // entity — see the note there.
  93:  { keys: ["defTD"], type: "def" },
  101: { keys: ["defTD"], type: "def" },
  102: { keys: ["defTD"], type: "def" },
  103: { keys: ["defTD"], type: "def" },
  104: { keys: ["defTD"], type: "def" },

  // Points-allowed ladder. NOT CONTIGUOUS — 89-92 then jumps to 121-125.
  // ESPN's buckets are 0 / 1-6 / 7-13 / 14-17 / 18-21 / 22-27 / 28-34 / 35-45 /
  // 46+; Mock1's are 0 / 1-6 / 7-13 / 14-20 / 21-27 / 28-34 / 35+.
  89:  { keys: ["defPtsAllowed0"],     type: "def" },
  90:  { keys: ["defPtsAllowed1_6"],   type: "def" },
  91:  { keys: ["defPtsAllowed7_13"],  type: "def" },
  92:  { keys: ["defPtsAllowed14_20"], type: "def" },
  122: { keys: ["defPtsAllowed21_27"], type: "def" },
  123: { keys: ["defPtsAllowed28_34"], type: "def" },
  124: { keys: ["defPtsAllowed35"],    type: "def" },
  // ESPN 121 (18-21) and 125 (46+) have NO Mock1 home — 121 straddles Mock1's
  // 14-20/21-27 boundary and 125 duplicates 35+. Dropped rather than folded in.
  //
  // The yards-allowed ladder (128-136) is dropped too: Mock1's scoring model
  // has no yards-allowed term. A league that scores yards allowed will have
  // understated D/ST values — surface that as an import note rather than
  // inventing a key.
};
